/**
 * `DesktopSidebar` — the member shell's navigation at desktop width, mounted by
 * `Layout` in place of the top bar, bottom nav and menu sheet.
 *
 * Top to bottom: the brand mark (the page `<h1>`, as `TopBar`'s is on mobile —
 * every route under `Layout` demotes its own heading to `<h2>` because the shell
 * owns the `<h1>`), the member card, the three item groups, sign-out, and the
 * CMS link for administrators.
 *
 * The items come from `sidebarGroups(NAV_ENTRIES, SERVICE_ENTRIES)` and are
 * greyed by `useNavItemGating`, the same sources and rules as the mobile chrome.
 * Nothing here names a route or a label of its own.
 *
 * Sub-menus start collapsed and keep their state across navigation, because
 * `Layout` (and with it this component) stays mounted between member routes.
 */

import { type JSX, useId, useState } from 'react';
import { Link, useLocation } from 'react-router';

import ytkLogo from '../assets/ytk-logo.png';
import { useTranslate } from '../i18n/useTranslate.js';
import { useUnreadCount } from '../member-cases/useUnreadCount.js';
import { useSession } from '../session/useSession.js';

import { DesktopFeeOverdueMarker } from './DesktopFeeOverdueMarker.js';
import { DesktopSidebarItem } from './DesktopSidebarItem.js';
import { SignOutIcon } from './icons.js';
import { MemberChipContent, useShellSignOut } from './MenuSheetHelpers.js';
import { menuCmsClasses } from './menuClasses.js';
import { type ExpandableKey, SERVICE_ENTRIES } from './menuServiceEntries.js';
import { NAV_ENTRIES, type NavItem, sidebarGroups, useNavItemGating } from './navEntries.js';

const CMS_TO = '/admin/cms';
const MESSAGES_PATH = '/messages';
const PROFILE_PATH = '/profile';

// Above the page content and the back header (z-30), below dialogs (z-50).
// The spacing and text sizes are the prototype's desktop sidebar, not the menu
// sheet's: rows run edge to edge, so the frame itself has no padding.
const asideClasses =
  'fixed top-0 bottom-0 left-0 z-40 flex w-desktop-sidebar flex-col overflow-y-auto ' +
  'border-r border-border bg-card';
const titleClasses = 'm-0 flex h-top-bar shrink-0 items-center px-4 pt-3';
const brandLinkClasses =
  'inline-flex items-center rounded-md no-underline transition-opacity hover:opacity-80 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const brandLogoClasses = 'h-12 w-auto';
const memberCardClasses =
  'mx-3 mt-2 flex items-center rounded-lg p-2 text-left no-underline transition-colors ' +
  'hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const memberNameClasses = 'truncate text-base font-medium text-foreground';
const memberNumberClasses = 'text-base text-muted-foreground';
const navClasses = 'flex flex-col py-2';
const groupClasses = 'm-0 flex list-none flex-col gap-0.5 p-0';
const dividerClasses = 'mx-4 my-1.5 border-0 border-t border-border';
const footerDividerClasses = 'mx-4 border-0 border-t border-border';
const footerClasses = 'mt-auto flex flex-col gap-2 pt-2 pb-2';
const signOutClasses =
  'flex w-full items-center gap-3 border-l-2 border-transparent px-4 py-1.5 text-left ' +
  'text-base font-medium text-foreground/80 cursor-pointer transition-colors ' +
  'hover:bg-foreground/4 hover:text-foreground ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring';
const cmsWrapClasses = 'px-3';

const GROUPS = sidebarGroups(NAV_ENTRIES, SERVICE_ENTRIES);

function itemKey(item: NavItem): string {
  return item.source === 'nav' ? item.entry.path : item.entry.key;
}

export function DesktopSidebar(): JSX.Element {
  const t = useTranslate();
  const { pathname } = useLocation();
  const { state } = useSession();
  const isGreyed = useNavItemGating();
  const unreadCount = useUnreadCount();
  const signOut = useShellSignOut();
  const [openSubmenus, setOpenSubmenus] = useState<ReadonlySet<ExpandableKey>>(() => new Set());

  // A new expandable key without an id here fails to compile.
  const lakikaveriSubmenuId = useId();
  const palvelutEdutSubmenuId = useId();
  const submenuIdsByKey: Record<ExpandableKey, string> = {
    lakikaveri: lakikaveriSubmenuId,
    palvelutEdut: palvelutEdutSubmenuId,
  };

  const toggleSubmenu = (key: ExpandableKey): void => {
    setOpenSubmenus((current) => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const isSignedIn = state.kind === 'member' || state.kind === 'admin';

  const renderGroup = (items: readonly NavItem[]): JSX.Element => (
    <ul className={groupClasses}>
      {items.map((item) => {
        const expandable =
          item.source === 'service' && item.entry.kind === 'expandable' ? item.entry.key : null;
        return (
          <li key={itemKey(item)}>
            <DesktopSidebarItem
              item={item}
              pathname={pathname}
              greyed={isGreyed(item)}
              unreadCount={
                item.source === 'nav' && item.entry.path === MESSAGES_PATH ? unreadCount : 0
              }
              {...(item.source === 'nav' && item.entry.path === PROFILE_PATH
                ? { marker: <DesktopFeeOverdueMarker /> }
                : {})}
              {...(expandable === null
                ? {}
                : {
                    isOpen: openSubmenus.has(expandable),
                    onToggle: () => {
                      toggleSubmenu(expandable);
                    },
                    submenuId: submenuIdsByKey[expandable],
                  })}
            />
          </li>
        );
      })}
    </ul>
  );

  return (
    <aside className={asideClasses}>
      <h1 className={titleClasses}>
        <Link to="/" className={brandLinkClasses}>
          <img src={ytkLogo} alt={t('common.brandFullName')} className={brandLogoClasses} />
          <span className="sr-only">{t('shell.desktopSidebar.homeLink')}</span>
        </Link>
      </h1>

      {isSignedIn && (
        <Link to="/profile" className={memberCardClasses}>
          <MemberChipContent
            avatar={false}
            numberFallback={null}
            hideNumberOnError
            nameClassName={memberNameClasses}
            numberClassName={memberNumberClasses}
          />
        </Link>
      )}

      <nav aria-label={t('shell.desktopSidebar.navLabel')} className={navClasses}>
        {/* Polite live region, as in `PrimaryNav`: the bottom nav (and its
            region) is not mounted at desktop width, and the pill's text is
            static, so a change in the count would otherwise be silent. Empty
            at zero so clearing the count announces nothing. */}
        <span role="status" className="sr-only">
          {unreadCount > 0
            ? t('shell.primaryNav.unreadBadge').replace('{n}', String(unreadCount))
            : ''}
        </span>
        {renderGroup(GROUPS.main)}
        <hr className={dividerClasses} />
        {renderGroup(GROUPS.footer)}
        <hr className={dividerClasses} />
        {renderGroup(GROUPS.secondary)}
      </nav>

      {isSignedIn && (
        <div className={footerClasses}>
          <hr className={footerDividerClasses} />
          <button type="button" className={signOutClasses} onClick={() => void signOut()}>
            <SignOutIcon aria-hidden="true" className="h-5 w-5 shrink-0" />
            <span>{t('shell.menu.signOut')}</span>
          </button>
          {state.kind === 'admin' && (
            <div className={cmsWrapClasses}>
              <Link to={CMS_TO} className={`${menuCmsClasses} w-full`}>
                {t('shell.menu.cms')}
              </Link>
            </div>
          )}
        </div>
      )}
    </aside>
  );
}
